import { useQuery } from '@tanstack/react-query';

import { api } from './api-client';
import { decodeJwtPayload } from './auth';
import { useAuthStore, type AuthUser } from '../stores/auth-store';

export interface LoginPayload {
  email: string;
  password: string;
}

export interface RegisterPayload {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
}

export interface CreateOrganizationPayload {
  name: string;
  slug: string;
}

export interface AuthResponse {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

export interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
}

export interface OrganizationSummary {
  id: string;
  name: string;
  slug: string;
  planId: string;
  status: string;
  createdAt: string;
}

export function useMeQuery() {
  const token = useAuthStore((state) => state.token);

  return useQuery({
    queryKey: ['auth', 'me'],
    enabled: Boolean(token),
    queryFn: async () => {
      const response = await api.get<AuthUser>('/auth/me');
      return response.data;
    },
  });
}

export function useOrganizationQuery(orgId: string | null) {
  return useQuery({
    queryKey: ['organizations', orgId],
    enabled: Boolean(orgId),
    queryFn: async () => {
      const response = await api.get<OrganizationSummary>(`/organizations/${orgId}`);
      return response.data;
    },
  });
}

export function applyAuthResponse(payload: AuthResponse): void {
  const decoded = decodeJwtPayload(payload.accessToken);

  useAuthStore.getState().setAuth({
    user: payload.user,
    token: payload.accessToken,
    refreshToken: payload.refreshToken,
    orgId: decoded?.orgId ?? null,
    role: decoded?.role ?? null,
  });
}
